import { alpha, type Theme } from '@mui/material/styles';

/** App shell: fixed top toolbar (primary + optional secondary row). */
export interface AppToolbarChromeColors {
  barBg: string;
  barBorder: string;
  secondaryBg: string;
  secondaryBorder: string;
  iconColor: string;
  iconHoverColor: string;
  iconHoverBg: string;
}

export function getAppToolbarChrome(theme: Theme): AppToolbarChromeColors {
  const isDark = theme.palette.mode === 'dark';
  return {
    barBg: theme.palette.background.paper,
    barBorder: theme.palette.divider,
    secondaryBg: isDark
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.02),
    secondaryBorder: theme.palette.divider,
    iconColor: theme.palette.text.secondary,
    iconHoverColor: theme.palette.text.primary,
    iconHoverBg: isDark
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.04),
  };
}

/** App shell: drawer, nav list, popover menu. */
export interface NavChromeColors {
  itemHoverBg: string;
  itemSelectedBg: string;
  itemSelectedHoverBg: string;
  nestedListBg: string;
  nestedListBorder: string;
  drawerBorder: string;
  drawerBgLight: string;
  drawerBgDark: string;
  popoverShadow: string;
  popoverSelectedBg: string;
}

export function getNavChromeColors(theme: Theme): NavChromeColors {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary;
  return {
    itemHoverBg: isDark
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.04),
    itemSelectedBg: isDark
      ? alpha(primary.light, 0.16)
      : alpha(primary.main, 0.12),
    itemSelectedHoverBg: isDark
      ? alpha(primary.light, 0.24)
      : alpha(primary.main, 0.16),
    nestedListBg: isDark ? alpha(theme.palette.common.white, 0.00) : 'transparent',
    nestedListBorder: isDark ? alpha(theme.palette.common.white, 0.08) : 'transparent',
    drawerBorder: isDark
      ? alpha(theme.palette.common.white, 0.12)
      : alpha(theme.palette.common.black, 0.12),
    drawerBgLight: theme.palette.background.default,
    drawerBgDark: alpha(theme.palette.common.black, 0.2),
    popoverShadow: `0 8px 16px ${alpha(theme.palette.common.black, isDark ? 0.4 : 0.15)}`,
    popoverSelectedBg: isDark
      ? alpha(primary.light, 0.16)
      : alpha(primary.main, 0.12),
  };
}

/** Image lightbox / gallery scrims. */
export interface MediaScrimColors {
  backdrop: string;
  control: string;
  controlHover: string;
  footer: string;
  iconMuted: string;
}

export function getMediaScrimColors(theme: Theme): MediaScrimColors {
  const black = theme.palette.common.black;
  const white = theme.palette.common.white;
  return {
    backdrop: alpha(black, 0.9),
    control: alpha(black, 0.5),
    controlHover: alpha(black, 0.7),
    footer: alpha(black, 0.8),
    iconMuted: alpha(white, 0.54),
  };
}

/** EnhancedTable row selection + loading overlay. */
export interface TableSelectionColors {
  selectedRowBg: string;
  loadingOverlay: string;
  bulkActionHoverBg: string;
}

export function getTableSelectionColors(theme: Theme): TableSelectionColors {
  const isDark = theme.palette.mode === 'dark';
  return {
    selectedRowBg: isDark
      ? alpha(theme.palette.primary.light, 0.18)
      : alpha(theme.palette.primary.main, 0.14),
    loadingOverlay: isDark
      ? alpha(theme.palette.common.black, 0.4)
      : alpha(theme.palette.common.white, 0.4),
    bulkActionHoverBg: alpha(theme.palette.common.white, 0.1),
  };
}

/** EFEM / global lock banners and dialogs. */
export interface LockBannerChromeColors {
  glassBg: string;
  progressTrack: string;
  progressBar: string;
  progressBarMuted: string;
  detailBg: string;
  warningTintBg: string;
  warningTintBorder: string;
  dialogShadow: string;
}

export function getLockBannerChromeColors(theme: Theme): LockBannerChromeColors {
  const warning = theme.palette.warning.main;
  return {
    glassBg: alpha(theme.palette.common.white, 0.2),
    progressTrack: alpha(theme.palette.common.white, 0.9),
    progressBar: alpha(theme.palette.common.white, 0.7),
    progressBarMuted: alpha(theme.palette.common.white, 0.1),
    detailBg: alpha(theme.palette.common.white, 0.15),
    warningTintBg: alpha(warning, theme.palette.mode === 'dark' ? 0.15 : 0.1),
    warningTintBorder: alpha(warning, theme.palette.mode === 'dark' ? 0.5 : 0.35),
    dialogShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.24)}`,
  };
}

/** Detail panel split divider wash. */
export function getPanelDividerBg(theme: Theme): string {
  return theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.14)
    : alpha(theme.palette.common.black, 0.1);
}

/** Master–detail panel backdrop while resizing. */
export function getPanelResizeBackdrop(theme: Theme): string {
  return theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.black, 0.35)
    : alpha(theme.palette.common.white, 0.5);
}

/** Provider device fleet / ops dashboard status chips. */
export interface DeviceStatusColorMaps {
  iot: Record<string, string>;
  nonIot: Record<string, string>;
  order: Record<string, string>;
  health: {
    total: string;
    healthy: string;
    degraded: string;
    unhealthy: string;
    unknown: string;
    needsUpdate: string;
    staleCache: string;
  };
}

export function getDeviceStatusColorMaps(theme: Theme): DeviceStatusColorMaps {
  const p = theme.palette;
  return {
    iot: {
      manufactured: p.primary.main,
      'ready-to-provision': p.warning.main,
      provisioned: p.success.main,
      decommissioned: p.grey[600],
    },
    nonIot: {
      'in-stock': p.grey[600],
      allocated: p.warning.main,
      shipped: p.primary.main,
      claimed: p.primary.main,
      active: p.success.main,
      returned: p.warning.dark,
      retired: p.grey[600],
    },
    order: {
      draft: p.grey[600],
      confirmed: p.info.main,
      shipped: p.info.main,
      completed: p.success.main,
      cancelled: p.error.main,
    },
    health: {
      total: p.primary.main,
      healthy: p.success.dark,
      degraded: p.warning.main,
      unhealthy: p.error.main,
      unknown: p.grey[600],
      needsUpdate: p.warning.main,
      staleCache: p.warning.dark,
    },
  };
}

export function deviceStatusColor(
  maps: DeviceStatusColorMaps,
  key: string,
  map: 'iot' | 'nonIot' | 'order',
  theme: Theme,
): string {
  const table = map === 'iot' ? maps.iot : map === 'nonIot' ? maps.nonIot : maps.order;
  return table[key] ?? theme.palette.grey[600];
}

/** Conflict statistics charts (Recharts). */
export interface ConflictChartColors {
  byType: Record<string, string>;
  bySeverity: Record<string, string>;
  byStatus: Record<string, string>;
  pieDefault: string;
  trendCreated: string;
  trendResolved: string;
}

export function getConflictChartColors(theme: Theme): ConflictChartColors {
  const p = theme.palette;
  return {
    byType: {
      GhostWafer: p.error.main,
      MissingWafer: p.warning.main,
      Mismatch: p.warning.dark,
      Lost: p.secondary.main,
      Duplicate: p.info.main,
    },
    bySeverity: {
      Critical: p.error.dark,
      High: p.warning.dark,
      Medium: p.warning.light,
      Low: p.success.dark,
    },
    byStatus: {
      Open: p.error.main,
      UnderReview: p.warning.main,
      Resolved: p.success.main,
      Dismissed: p.grey[500],
      Escalated: p.secondary.main,
    },
    pieDefault: p.grey[500],
    trendCreated: p.error.main,
    trendResolved: p.success.main,
  };
}

/** Legacy LocationTracking dashboard (advanced). */
export interface LocationTrackingColors {
  zoneColors: string[];
  statusColors: Record<string, string>;
  gridStroke: string;
}

export function getLocationTrackingColors(theme: Theme): LocationTrackingColors {
  const p = theme.palette;
  return {
    zoneColors: [
      p.success.main,
      p.info.main,
      p.warning.main,
      p.secondary.main,
    ],
    statusColors: {
      Stationary: p.success.main,
      Moving: p.warning.main,
      'In Transit': p.info.main,
      Unknown: p.error.main,
    },
    gridStroke: p.grey[300],
  };
}

export function locationTrackingStatusColor(
  status: string,
  colors: LocationTrackingColors,
  theme: Theme,
): string {
  return colors.statusColors[status] ?? theme.palette.grey[600];
}

/** Stripe Elements appearance (AddPaymentMethodDialog). */
export interface StripeElementColors {
  text: string;
  placeholder: string;
  danger: string;
  containerBg: string;
  containerBgHover: string;
  containerBgFocus: string;
}

export function getStripeElementColors(theme: Theme): StripeElementColors {
  const isDark = theme.palette.mode === 'dark';
  return {
    text: isDark ? theme.palette.common.white : theme.palette.common.black,
    placeholder: isDark ? theme.palette.grey[500] : theme.palette.grey[600],
    danger: theme.palette.error.main,
    containerBg: isDark
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    containerBgHover: isDark
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.04),
    containerBgFocus: isDark
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.04),
  };
}

/** Temp gallery / workflow preview accents. */
export interface AccentSurfaceColors {
  warningTintBg: string;
  warningBorder: string;
  infoGradientStart: string;
  infoGradientEnd: string;
  rowHoverBg: string;
  jobDetailHighlightBg: string;
  auditRowBg: string;
  planUpgradeTint: string;
  monitoringGlass: string;
}

export function getAccentSurfaceColors(theme: Theme): AccentSurfaceColors {
  const isDark = theme.palette.mode === 'dark';
  const info = theme.palette.info.main;
  return {
    warningTintBg: isDark
      ? alpha(theme.palette.warning.main, 0.15)
      : theme.palette.warning.light,
    warningBorder: theme.palette.warning.main,
    infoGradientStart: isDark
      ? alpha(info, 0.08)
      : alpha(info, 0.03),
    infoGradientEnd: isDark ? alpha(info, 0.01) : alpha(info, 0),
    rowHoverBg: isDark
      ? alpha(theme.palette.common.white, 0.05)
      : 'transparent',
    jobDetailHighlightBg: alpha(theme.palette.common.white, 0.15),
    auditRowBg: isDark
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.02),
    planUpgradeTint: alpha(theme.palette.primary.main, 0.08),
    monitoringGlass: alpha(theme.palette.common.white, 0.8),
  };
}

/** Extend map/location SVG overlays (shared with mapOverlayColors). */
export interface MapSvgOverlayExtras {
  labelHaloStroke: string;
  tagShadow: string;
  trailStartFill: string;
  trailEndFill: string;
  anchorHighlightFill: string;
  anchorLabelFill: string;
  uwbGroupPillFill: string;
  canvasDimFill: string;
  canvasDimStroke: string;
  tabActiveBorder: string;
}

export function getMapSvgOverlayExtras(theme: Theme): MapSvgOverlayExtras {
  const p = theme.palette;
  const isDark = theme.palette.mode === 'dark';
  return {
    labelHaloStroke: alpha(p.common.white, isDark ? 0.85 : 0.9),
    tagShadow: alpha(p.common.black, isDark ? 0.35 : 0.2),
    trailStartFill: alpha(p.success.main, 0.15),
    trailEndFill: alpha(p.error.main, 0.15),
    anchorHighlightFill: alpha(p.info.light, isDark ? 0.14 : 0.1),
    anchorLabelFill: p.common.white,
    uwbGroupPillFill: isDark
      ? alpha(p.common.white, 0.14)
      : alpha(p.grey[900], 0.85),
    canvasDimFill: alpha(p.common.black, isDark ? 0.45 : 0.58),
    canvasDimStroke: alpha(p.common.black, isDark ? 0.28 : 0.4),
    tabActiveBorder: isDark
      ? alpha(p.common.white, 0.35)
      : alpha(p.common.black, 0.35),
  };
}

/** FOUP slot grid chrome not covered by FoupRackColors. */
/** CSS custom properties for `AmplifyAuthWrapper.css` (set on `document.documentElement`). */
export function getAmplifyAuthSurfaceVars(theme: Theme): Record<string, string> {
  const p = theme.palette;
  const isDark = theme.palette.mode === 'dark';
  return {
    '--auth-header-title': isDark ? p.info.light : p.primary.main,
    '--auth-text-secondary': p.text.secondary,
    '--auth-body-bg': p.background.default,
    '--auth-body-fg': p.text.primary,
    '--auth-link': p.primary.main,
    '--auth-link-hover': isDark ? p.primary.light : p.primary.dark,
    '--auth-primary-main': p.primary.main,
    '--auth-primary-dark': p.primary.dark,
    '--auth-primary-light': p.primary.light,
    '--auth-radius-control': '8px',
    '--auth-radius-card': '12px',
    '--auth-amplify-bg-secondary': p.background.paper,
    '--auth-amplify-border': isDark ? alpha(p.common.white, 0.08) : p.divider,
    '--auth-amplify-font-primary': p.text.primary,
    '--auth-amplify-font-secondary': p.text.secondary,
    '--auth-input-bg': p.background.paper,
    '--auth-input-border': isDark ? p.grey[700] : p.divider,
    '--auth-input-fg': p.text.primary,
    '--auth-placeholder': p.text.disabled,
    '--auth-focus-ring': alpha(p.primary.main, 0.25),
    '--auth-button-shadow': `0 2px 4px ${alpha(p.primary.main, 0.35)}`,
    '--auth-button-shadow-hover': `0 4px 8px ${alpha(p.primary.main, 0.45)}`,
    '--auth-card-shadow': isDark
      ? `0 2px 8px ${alpha(p.common.black, 0.4)}`
      : `0 2px 8px ${alpha(p.common.black, 0.1)}`,
    '--auth-router-shadow': isDark
      ? `0 2px 8px ${alpha(p.common.black, 0.4)}`
      : `0 2px 8px ${alpha(p.common.black, 0.1)}`,
  };
}

export function getFoupSlotGridChrome(theme: Theme, foupSelectedGlow: string): {
  borderSubtle: string;
  selectedGlow: string;
  textShadow: string;
  textShadowOccupied: string;
} {
  return {
    borderSubtle: alpha(theme.palette.common.white, 0.12),
    selectedGlow: foupSelectedGlow,
    textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.45)}`,
    textShadowOccupied: `0 1px 1px ${alpha(theme.palette.common.black, 0.35)}`,
  };
}
